import React from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import { CrewHistoryFlow, auditActionLabel } from './ReplacementAuditFlow';
import type { CrewHistoryResponse } from './types';

export { auditActionLabel };

const CrewHistoryPanel: React.FC<{ crewId: number }> = ({ crewId }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const url = crewId > 0 ? `/crew/${crewId}/history?take=200` : null;
  const { data, error, isLoading } = useSWR<CrewHistoryResponse>(url, fetcher);

  if (crewId <= 0) {
    return (
      <p className={cn('text-sm', colors.textSecondary)}>
        Save the crew record first to see the audit log.
      </p>
    );
  }

  if (isLoading) {
    return <p className={cn('text-sm', colors.textSecondary)}>Loading audit log…</p>;
  }

  if (error) {
    return (
      <p className={cn('text-sm', isDark ? 'text-red-400' : 'text-red-600')}>
        Could not load the audit log.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className={cn('text-sm', colors.textSecondary)}>
          Audit trail for crew file edits and crew-change / relief actions.
        </p>
        <Link
          to="/crew/replacement-planner"
          className={cn('text-sm hover:underline shrink-0', colors.primaryFg)}
        >
          Open replacement planner
        </Link>
      </div>

      <CrewHistoryFlow
        rows={data?.rows ?? []}
        groupReplacements
        showFilters
        emptyMessage="No audit log yet. Save the crew file or plan a replacement to start the trail."
      />
    </div>
  );
};

export default CrewHistoryPanel;
